import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AinstainWidgetService {
  private readonly renderer: Renderer2;
  private chatExternalScript: HTMLElement | null = null;
  private chatInlineScript: HTMLElement | null = null;
  private routerSubscription: Subscription | null = null;
  private destroyed = false;

  constructor(private readonly router: Router, rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  init(uuid: string, userId: string): void {
    this.loadChatWidget(uuid, userId);
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url.includes('/login')) {
          this.destroyChatWidget();
          this.routerSubscription?.unsubscribe();
        } else {
          this.hideChatWidget();
        }
      }
    });
  }

  show(): void {
    this.showChatWidget();
  }

  private loadChatWidget(uuid: string, userId: string): void {
    const serviceName = environment.AINSTEIN_SMART_WEBCOMPONENT.SERVICE_NAME;
    const equipo = (environment.AINSTEIN_SMART_WEBCOMPONENT as any).TEAM || userId || 'equipo_prueba';

    const generarUUID = (): string => {
      try {
        if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) {
          return (crypto as any).randomUUID();
        }

        if (typeof crypto !== 'undefined' && (crypto as any).getRandomValues) {
          const bytes = new Uint8Array(16);
          (crypto as any).getRandomValues(bytes);
          bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
          bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant
          const hex: string[] = Array.from(bytes, (b) => ('0' + b.toString(16)).slice(-2));
          return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
        }
      } catch (e) {
        console.error('Error generarUUID ChatWidget:', e);
      }

      let ts = new Date().getTime() >>> 0;
      const xorshift32 = () => {
        ts ^= (ts << 13) >>> 0;
        ts ^= ts >>> 17;
        ts ^= (ts << 5) >>> 0;
        // normalize to [0, 1)
        return (ts >>> 0) / 0xffffffff;
      };

      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (ts + Math.floor(xorshift32() * 16)) % 16 | 0;
        ts = Math.floor(ts / 16) >>> 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    };

    const obtenerFecha = (): string => {
      const hoy = new Date();
      const yyyy = hoy.getFullYear();
      const mm = String(hoy.getMonth() + 1).padStart(2, '0');
      const dd = String(hoy.getDate()).padStart(2, '0');
      return `${yyyy}${mm}${dd}`;
    };

    const construirSessionId = (service: string, equipoParam: string): string => {
      const id = generarUUID();
      const fecha = obtenerFecha();
      return `${id}&${service}&${fecha}&${equipoParam}`;
    };

    const idSesionComp = construirSessionId(serviceName, equipo);

    const config: any = {
      environment: environment.AINSTEIN_SMART_WEBCOMPONENT.ENVIROMENT,
      logo: environment.AINSTEIN_SMART_WEBCOMPONENT.LOGO,
      title: environment.AINSTEIN_SMART_WEBCOMPONENT.TITLE,
      primaryColor: '#0043A9',
      service: serviceName,
      id_sesion: idSesionComp,
      welcomeMessage: environment.AINSTEIN_SMART_WEBCOMPONENT.WELCOME_MESSAGE,
      position:'bottom-right',
      mobileFullscreen: false,
      mobileBottomOffset: 20,
      desktopBottomOffset: 80,
      desktopRightOffset: 80,
    };

    if ((window as any).ChatWidget) {
      this.showChatWidget();
      return;
    }

    if (!document.body) {
      return;
    }

    const externalScript = this.renderer.createElement('script');
    externalScript.src =
      environment.AINSTEIN_SMART_WEBCOMPONENT.LIB_SCRIPT || 'https://ainstein.bancodebogota.com/chat-widget.js';
    externalScript.type = 'text/javascript';

    externalScript.onload = () => {
      try {
        if (!document.body || this.destroyed) {
          return;
        }

        if ((window as any).ChatWidget && typeof (window as any).ChatWidget.init === 'function') {
          try {
            (window as any).ChatWidget.init(config);
            this.showChatWidget();
            this.adjustWidgetLayout();
          } catch (e) {
            console.error('Error calling ChatWidget.init directly:', e);
          }
          return;
        }

        const inlineScript = this.renderer.createElement('script');
        inlineScript.type = 'text/javascript';
        inlineScript.text = `window.ChatWidget.init(${JSON.stringify(config)});`;
        document.body.appendChild(inlineScript);
        this.chatInlineScript = inlineScript;
        setTimeout(() => this.adjustWidgetLayout(), 500);
      } catch (e) {
        console.error('Error initializing ChatWidget:', e);
      }
    };

    try {
      document.body.appendChild(externalScript);
      this.chatExternalScript = externalScript;
    } catch (e) {
      console.error('Error appending external script:', e);}
  }

  private showChatWidget(): void {
    if (!document.body) {
      return;
    }
    const appRoot = document.querySelector('app-root');
    Array.from(document.body.children).forEach((child: any) => {
      if (child !== appRoot && child.tagName !== 'SCRIPT') {
        child.style.display = '';
      }
    });
  }

  private hideChatWidget(): void {
    if (!document.body) {
      return;
    }
    const appRoot = document.querySelector('app-root');
    Array.from(document.body.children).forEach((child: any) => {
      if (child !== appRoot && child.tagName !== 'SCRIPT') {
        child.style.display = 'none';
      }
    });
  }

  destroyChatWidget(): void {
    this.destroyed = true;
    if ((window as any).ChatWidget?.destroy) {
      (window as any).ChatWidget.destroy();
    }
    delete (window as any).ChatWidget;

    if (this.chatExternalScript && document.body) {
      this.renderer.removeChild(document.body, this.chatExternalScript);
      this.chatExternalScript = null;
    }

    if (this.chatInlineScript && document.body) {
      this.renderer.removeChild(document.body, this.chatInlineScript);
      this.chatInlineScript = null;
    }

    if (!document.body) {
      return;
    }

    const appRoot = document.querySelector('app-root');
    Array.from(document.body.children).forEach((child: any) => {
      if (child !== appRoot && child.tagName !== 'SCRIPT') {
        child.remove();
      }
    });
  }

  private adjustWidgetLayout(retries = 5, delay = 500): void {
    if (!document.body) {
      return;
    }

    const tryAdjust = (attempt: number) => {
      // heuristics: select elements likely created by the chat widget
      const selectors = [
        '[id*="chat" i]',
        '[class*="chat" i]',
        '[class*="ainstein" i]',
        '[class*="ChatWidget" i]'
      ];
      const nodes: Element[] = [];
      selectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => nodes.push(el));
      });

      if (nodes.length === 0) {
        if (attempt < retries) {
          setTimeout(() => tryAdjust(attempt + 1), delay);
        }
        return;
      }

      const nav = document.querySelector('nav') || document.querySelector('.navbar') || document.querySelector('.header');
      let navHeight = 64;
      if (nav instanceof HTMLElement) {
        navHeight = nav.getBoundingClientRect().height;
      }

      nodes.forEach((node) => {
        const el = node as HTMLElement;
        if (el.tagName === 'SCRIPT') {
          return;
        }
        el.style.maxHeight = `calc(100vh - ${Math.ceil(navHeight + 16)}px)`;
        el.style.overflowY = 'auto';
        el.style.zIndex = '9999';
        if (window.innerWidth <= 768) {
          el.style.position = el.style.position || 'fixed';
        }
      });
    };

    tryAdjust(0);
  }
}
